import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Service, serviceClass, ServiceItem } from "../../../services/Layanan/ListLayanan";
import Navbar from "../../../component/includes/navbar";
import Footer from "../../../component/includes/footer";

const ServicePage: React.FC = () => {
  const { serviceSlug } = useParams<{ serviceSlug: string }>();
  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ title: string; message: string } | null>(null);
  const [mainImageLoaded, setMainImageLoaded] = useState(false);

  useEffect(() => {
    const fetchService = async () => {
      if (!serviceSlug) {
        setError({
          title: "Halaman Tidak Valid",
          message: "URL layanan tidak valid. Silakan periksa kembali URL yang Anda masukkan.",
        });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const createSlug = (name: string): string =>
          encodeURIComponent(
            name
              .toLowerCase()
              .replace(/\s+/g, "-")
              .replace(/[^\w-&]+/g, "")
              .replace(/-+/g, "-")
              .replace(/^-+/, "")
              .replace(/-+$/, "")
          );

        const allServices = await serviceClass.getAllServices();
        let targetServiceId: number | null = null;
        let targetServiceName = "";

        for (const s of allServices) {
          if (createSlug(s.name) === serviceSlug || s.name === serviceSlug || s.name.toLowerCase() === serviceSlug.toLowerCase()) {
            targetServiceId = s.id;
            targetServiceName = s.name;
            break;
          }
        }

        if (targetServiceId) {
          const detailedService = await serviceClass.getServiceByID(targetServiceId);
          if (detailedService) {
            setService(detailedService);
          } else {
            setError({
              title: "Layanan Tidak Ditemukan",
              message: `Maaf, layanan "${targetServiceName}" tidak ditemukan dalam sistem kami.`,
            });
          }
        } else {
          setError({
            title: "Layanan Tidak Ditemukan",
            message: `Maaf, layanan tidak ditemukan dalam sistem kami.`,
          });
        }
      } catch (err) {
        console.error("Error loading service:", err);
        setError({
          title: "Gagal Memuat Layanan",
          message: "Terjadi kesalahan saat memuat detail layanan. Silakan coba beberapa saat lagi.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [serviceSlug]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-pulse flex items-center gap-2">
          <div className="h-2 w-2 bg-blue-600 rounded-full animate-bounce"></div>
          <div className="h-2 w-2 bg-blue-600 rounded-full animate-bounce delay-75"></div>
          <div className="h-2 w-2 bg-blue-600 rounded-full animate-bounce delay-150"></div>
          <span className="text-gray-600 ml-2">Memuat detail layanan...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <h3 className="text-red-800 font-semibold text-lg mb-2">{error.title}</h3>
            <p className="text-red-600 mb-4">{error.message}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Coba Lagi
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!service) {
    return null;
  }

  const whatsappLink = `https://wa.me/${service.phone_number}?text=Halo, saya ingin bertanya lebih lanjut tentang layanan ${service.name}`;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow mt-20">
        <div className="container mx-auto px-4 py-8">
          <article className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">{service.name}</h1>

            <div className="mb-8">
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-green-500 text-white font-semibold rounded-lg shadow-md hover:bg-green-600 transition-colors"
              >
                Hubungi Kami di WhatsApp
              </a>
            </div>

            {service.main_image && (
              <div className="mb-8 relative">
                <div className={`transition-opacity duration-300 ${mainImageLoaded ? 'opacity-100' : 'opacity-0'}`}>
                  <img
                    src={`${process.env.REACT_APP_API_URL}${service.main_image}`}
                    alt={service.name}
                    className="w-full rounded-lg shadow-md max-h-[600px] object-contain bg-gray-50"
                    onLoad={() => setMainImageLoaded(true)}
                  />
                </div>
                {!mainImageLoaded && (
                  <div className="absolute inset-0 bg-gray-100 animate-pulse rounded-lg" />
                )}
              </div>
            )}

            <div className="prose prose-lg max-w-none">
              <div
                className="space-y-6 text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: service.description }}
              />
            </div>

            {(service.gallery_image?.length > 0 || service.images?.length > 0) && (
              <div className="mt-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Galeri Layanan</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(service.gallery_image || service.images).map((image: ServiceItem) => (
                    <div key={image.id} className="relative aspect-square bg-gray-50">
                      <img
                        src={`${process.env.REACT_APP_API_URL}${image.image}`}
                        alt={`${service.name} gallery`}
                        className="absolute inset-0 w-full h-full object-contain rounded-lg shadow-md"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ServicePage;